import classNames from 'classnames';
import type { AllHTMLAttributes, ReactElement, ReactNode } from 'react';

import { atoms } from '../../css/atoms/atoms.css';
import { Box } from '../Box/Box';
import { Loader } from '../Loader/Loader';
import type { TextProps } from '../Text/Text';
import { Text } from '../Text/Text';
import { buttonStyle, hidden, loaderContainer } from './Button.css';

type NativeButtonProps = AllHTMLAttributes<HTMLButtonElement>;

type ButtonSize = 'small' | 'regular' | 'large' | 'fixedSmall' | 'iconOnly' | 'large25';

type ButtonVariant =
  | 'solid'
  | 'outline'
  | 'text'
  | 'brand'
  | 'brandOutline'
  | 'danger'
  | 'dangerOutline'
  | 'solid25'
  | 'outline25'
  | 'text25';

export interface ButtonProps extends Omit<NativeButtonProps, 'height' | 'width' | 'size'> {
  children?: ReactNode;
  /** Controls the design of the button:
   * - `'solid'`
   * - `'outline'`
   * - `'text'`
   *
   * Defaults to a 'solid'
   */
  variant?: ButtonVariant;
  /** Used this to position the icon before button text */
  iconLeading?: ReactElement;
  /** Used this to position the icon after button text */
  iconTrailing?: ReactElement;
  /**
   * If set to `true` the button content is replaced with a loading animation.
   *
   * The button is also disabled.
   */
  loading?: boolean;
  /**
   * Used this to control what size the button renders
   * Currently two sizes are supported:
   * - regular
   * - small
   */
  size?: ButtonSize;
  as?: 'button' | 'a';
  type?: 'button' | 'reset' | 'submit';
  width?: 'full';
}

export interface ButtonTextProps {
  children?: ReactNode;
  variant: ButtonVariant;
  iconLeading?: ReactElement;
  iconTrailing?: ReactElement;
  loading?: boolean;
  size?: ButtonSize;
  disabled: boolean | undefined;
}

const resolveTextTone: {
  [key in ButtonVariant]: TextProps['tone'];
} = {
  solid: 'neutralInverted',
  outline: 'neutral',
  danger: 'neutralInverted',
  dangerOutline: 'danger',
  brand: 'neutralInverted',
  brandOutline: 'brandAccent',
  text: 'neutral',
  solid25: 'disabled25',
  outline25: 'contentSecondary25',
  text25: 'neutral',
};

const ButtonText = ({
  variant,
  size,
  iconLeading,
  iconTrailing,
  loading,
  children,
  disabled,
}: ButtonTextProps) => {
  const textSize =
    size === 'large25' ? 'large' : size === 'small' || size === 'fixedSmall' ? 'small' : 'medium';
  let textTone: TextProps['tone'];
  if (variant === 'solid25') {
    textTone = disabled ? 'disabled25' : 'contentInversePrimary25';
  } else if (variant === 'outline25') {
    textTone = disabled ? 'disabled25' : 'contentSecondary25';
  } else if (variant === 'text25') {
    textTone = disabled ? 'disabled25' : 'contentSecondary25';
  } else {
    textTone = resolveTextTone[variant];
  }
  return (
    <>
      {iconLeading || iconTrailing ? (
        <Text size={textSize} weight="demibold" tone={textTone} className={loading ? hidden : ''}>
          {!!iconLeading && (
            <span
              className={atoms({
                paddingRight: children ? 'xsmall' : 'none',
              })}
            >
              {iconLeading}
            </span>
          )}
          {children}
          {!!iconTrailing && (
            <span
              className={atoms({
                paddingLeft: children ? 'xsmall' : 'none',
              })}
            >
              {iconTrailing}
            </span>
          )}
        </Text>
      ) : (
        <Text size={textSize} weight="demibold" tone={textTone} className={loading ? hidden : ''}>
          {children}
        </Text>
      )}
    </>
  );
};

export const Button = ({
  ref,
  variant = 'solid',
  size = 'regular',
  loading,
  as = 'button',
  children,
  disabled,
  iconLeading,
  iconTrailing,
  width,
  className,
  ...restProps
}: ButtonProps & {
  ref?: React.Ref<HTMLButtonElement>;
}) => {
  const classNameWithButtonStyle = classNames(
    buttonStyle({
      size,
      variant,
    }),
    className,
  );

  return (
    <Box
      component={as}
      display="block"
      position="relative"
      textAlign="center"
      cursor={!loading && !disabled ? 'pointer' : undefined}
      disabled={disabled || loading}
      width={width}
      className={classNameWithButtonStyle}
      {...restProps}
      ref={ref}
    >
      <ButtonText
        size={size}
        variant={variant}
        loading={loading}
        iconLeading={iconLeading}
        iconTrailing={iconTrailing}
        disabled={disabled}
      >
        {children}
      </ButtonText>
      {loading && (
        <Text
          tone={resolveTextTone[variant]}
          className={classNames(loaderContainer, !loading ? hidden : '')}
        >
          <Loader size={size === 'regular' ? 'small' : 'xsmall'} />
        </Text>
      )}
    </Box>
  );
};

Button.displayName = 'Button';
